//src/features/vacancies/vacanciesSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

import { api } from 'services/api';

export interface Vacancy {
  id: number;
  title: string;
  description: string;
  category: string;
  grade: string;
  applications_total?: number;
  created_at?: string;
  apply_slug?: string;
  is_active?: boolean;
}

interface VacanciesState {
  items: Vacancy[];
  loading: boolean;
  error: string | null;
}

const initialState: VacanciesState = {
  items: [],
  loading: false,
  error: null,
};

export const fetchVacancies = createAsyncThunk(
  'vacancies/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api('vacancies');
      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Unknoown error occurred: fetchVacancies');
    }
  }
);

export const createVacancy = createAsyncThunk(
  'vacancies/create',
  async (vacancyData: Omit<Vacancy, 'id'>, { rejectWithValue }) => {
    try {
      const response = await api('vacancies/new', {
        method: 'POST',
        body: JSON.stringify(vacancyData),
      });
      return await response.json();
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('Unknoown error occurred: createVacancy');
    }
  }
);

const vacanciesSlice = createSlice({
  name: 'vacancies',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchVacancies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVacancies.fulfilled, (state, action: PayloadAction<Vacancy[]>) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchVacancies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createVacancy.fulfilled, (state, action: PayloadAction<Vacancy>) => {
        state.items.push(action.payload);
      });
  },
});

export default vacanciesSlice.reducer;
